/**
 * Layout contract for fixed bottom widgets (survey bar + chat launcher).
 * Consumed by `src/styles/globals.js` — keep selectors production-safe only.
 */
export const FLOATING_SURVEY_ROOT_SELECTOR = "div[data-testid='survey-root']"

/** App root scroll area — body padding does not scroll with `html/body { height: 100% }`. */
export const FLOATING_SCROLL_PADDING_TARGET_SELECTOR = '#app'

export const FLOATING_CHAT_LAUNCHER_STACK_SELECTORS = [
  '#launcher',
  '#launcher-frame',
  '[id^="web-messenger"]',
  '#spr-live-chat-app',
] as const

export const FLOATING_CHAT_LAUNCHER_SELECTOR =
  FLOATING_CHAT_LAUNCHER_STACK_SELECTORS.join(', ')

/** Scroll padding when the bottom survey bar is visible (chat-only needs none). */
export const MOBILE_SURVEY_SCROLL_PADDING_PX = 64

/** Bottom survey strip height — lifts corner launchers above the bar when both are visible. */
export const FLOATING_BOTTOM_BAR_HEIGHT_PX = 48

/** Measured at runtime by `floatingLayoutSync`; mock + prod stack CSS read the same var. */
export const FLOATING_SURVEY_BAR_HEIGHT_CSS_VAR =
  '--floating-survey-bar-height-px'

/** Corner launcher inset from viewport edges when no bottom bar is present. */
export const FLOATING_CORNER_LAUNCHER_BOTTOM_OFFSET_PX = 34

export const FLOATING_CORNER_LAUNCHER_HORIZONTAL_OFFSET_PX = 34

export const getFloatingCornerLauncherBottomWithBarPx = (): number =>
  FLOATING_BOTTOM_BAR_HEIGHT_PX + FLOATING_CORNER_LAUNCHER_BOTTOM_OFFSET_PX

type BuildCornerLauncherPositionCssParams = {
  launcherSelectors: readonly string[]
  cornerBottomOffsetPx: number
  cornerHorizontalOffsetPx: number
}

type BuildCornerLauncherStackCssParams =
  BuildCornerLauncherPositionCssParams & {
    surveySelector: string
    chatSelector: string
    bottomBarHeightPx: number
  }

/** Default corner inset when no bottom survey bar is present. */
export const buildCornerLauncherDefaultPositionCss = ({
  launcherSelectors,
  cornerBottomOffsetPx,
  cornerHorizontalOffsetPx,
}: BuildCornerLauncherPositionCssParams): string =>
  launcherSelectors
    .map(
      selector => `
  ${selector} {
    bottom: calc(
      ${cornerBottomOffsetPx}px + env(safe-area-inset-bottom, 0px)
    ) !important;
    right: calc(
      ${cornerHorizontalOffsetPx}px + env(safe-area-inset-right, 0px)
    ) !important;
  }`
    )
    .join('')

/**
 * Lifts third-party corner launchers above the bottom survey bar.
 * `!important` is required here: Zendesk/Sprinklr set inline `bottom` on their nodes.
 * Rules apply only when both widgets exist in the DOM.
 */
export const buildCornerLauncherStackCss = ({
  surveySelector,
  chatSelector,
  launcherSelectors,
  bottomBarHeightPx,
  cornerBottomOffsetPx,
  cornerHorizontalOffsetPx,
}: BuildCornerLauncherStackCssParams): string =>
  launcherSelectors
    .map(
      selector => `
  body:has(${surveySelector}):has(${chatSelector}) ${selector} {
    bottom: calc(
      var(${FLOATING_SURVEY_BAR_HEIGHT_CSS_VAR}, ${bottomBarHeightPx}px) + ${cornerBottomOffsetPx}px + env(safe-area-inset-bottom, 0px)
    ) !important;
    right: calc(
      ${cornerHorizontalOffsetPx}px + env(safe-area-inset-right, 0px)
    ) !important;
    transform: none !important;
  }`
    )
    .join('')
